"use client";

import { useState } from "react";
import { RefreshCw } from "lucide-react";
import { RequireRole } from "../console-shell";
import { api, commandId, formatTime, reasonText } from "../../lib/api";
import { Banner, Button, ButtonRow, ConfirmPanel, DeskCard, Desk, Field, FieldGrid, FormSection, Input, Note, PageHeader, SelectField, StatusTag } from ".";
import { ReadState, useRead } from "./record-tools";
import { usePublishOrganizations } from "./duty-workspace";

type Item = {
  id: string; organization: { id: string; name: string }; year: number; startMonth: number; endMonth: number; title: string; audienceLabel: string;
  note?: string; sourceRef: string; recordedAt: string; supersedesId?: string; withdrawnAt?: string; withdrawnReason?: string;
};
type Draft = { organizationId: string; startMonth: string; endMonth: string; title: string; audienceLabel: string; note: string; sourceRef: string; supersedes?: Item };

const MONTHS = Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: `${i + 1}月` }));
const span = (item: Pick<Item, "startMonth" | "endMonth">) => (item.startMonth === item.endMonth ? `${item.startMonth}月` : `${item.startMonth}—${item.endMonth}月`);

export function CalendarWorkspace() {
  return (
    <RequireRole roleCode="ACTIVITY_COORDINATOR">
      <CalendarDesk />
    </RequireRole>
  );
}

/**
 * 会务年历（重构方案 §4.5）。机关把年度工作要点里的节点录进来，会员首页「本季度重点」由它派生；
 * 没录就不出现。节点写下即冻结：要改就「替换」（旧的撤回并留在记录里），不要的「撤回」。
 */
function CalendarDesk() {
  const [year, setYear] = useState(() => new Date().getFullYear());
  const list = useRead<{ year: number; items: Item[] }>(`/api/v1/calendar?year=${year}`);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [showWithdrawn, setShowWithdrawn] = useState(false);
  const items = (list.data?.items ?? []).filter((i) => showWithdrawn || !i.withdrawnAt);
  const blank = (): Draft => ({ organizationId: "", startMonth: "1", endMonth: "1", title: "", audienceLabel: "全体会员", note: "", sourceRef: `${year}年度工作要点` });

  return (
    <Desk wide>
      <PageHeader
        eyebrow="会务与履职 / 会务年历"
        title="会务年历"
        description="把年度工作要点里的节点录成年历。会员首页的「本季度重点」只从这里来，没录就不出现。节点写下即定：要改用「替换」，旧的撤回并留在记录里。"
        actions={<ButtonRow>
          <Button variant="ghost" aria-label="刷新年历" onClick={() => void list.reload()}><RefreshCw size={16} /></Button>
          {!draft ? <Button onClick={() => setDraft(blank())}>录一个节点</Button> : null}
        </ButtonRow>}
      />
      {draft ? <NodeForm year={year} draft={draft} onCancel={() => setDraft(null)} onSaved={() => { setDraft(null); void list.reload(); }} /> : null}
      <DeskCard
        title={`${year}年`}
        description="按开始月份排。面向谁只是给会员看的一句话，不据此筛人。"
        actions={<ButtonRow>
          <Button variant="outline" size="sm" onClick={() => setYear(year - 1)}>上一年</Button>
          <Button variant="outline" size="sm" onClick={() => setYear(year + 1)}>下一年</Button>
          <Button variant="ghost" size="sm" onClick={() => setShowWithdrawn(!showWithdrawn)}>{showWithdrawn ? "只看有效的" : "含已撤回"}</Button>
        </ButtonRow>}
      >
        <ReadState {...list} retry={list.reload} />
        <ul className="list-none p-0">
          {items.map((item) => (
            <NodeRow key={item.id} item={item} onReplace={() => setDraft({ organizationId: item.organization.id, startMonth: String(item.startMonth), endMonth: String(item.endMonth), title: item.title, audienceLabel: item.audienceLabel, note: item.note ?? "", sourceRef: item.sourceRef, supersedes: item })} onChanged={() => void list.reload()} />
          ))}
        </ul>
        {list.data && !items.length ? <p className="px-3 py-6 text-xs text-muted-foreground">这一年还没有录入节点，会员首页不会出现「本季度重点」。</p> : null}
      </DeskCard>
    </Desk>
  );
}

function NodeRow({ item, onReplace, onChanged }: { item: Item; onReplace: () => void; onChanged: () => void }) {
  const [withdrawing, setWithdrawing] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const withdraw = async () => {
    setBusy(true); setError("");
    try {
      await api(`/api/v1/calendar/${item.id}/withdraw`, { method: "POST", body: { commandId: commandId(), reason: reason.trim() } });
      setWithdrawing(false);
      onChanged();
    } catch (caught) {
      setError(reasonText(caught));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="not-first:border-t px-3 py-3">
      <div className="flex items-start gap-4">
        <span className="w-20 shrink-0 text-sm font-semibold tabular-nums">{span(item)}</span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <span className={`text-sm font-semibold ${item.withdrawnAt ? "text-muted-foreground line-through" : ""}`}>{item.title}</span>
            {item.withdrawnAt ? <StatusTag tone="neutral">已撤回</StatusTag> : null}
          </div>
          <p className="text-xs text-muted-foreground">{item.organization.name} · 面向{item.audienceLabel}{item.note ? ` · ${item.note}` : ""}</p>
          <p className="text-xs text-muted-foreground">依据：{item.sourceRef} · 录入于 {formatTime(item.recordedAt)}{item.withdrawnReason ? ` · 撤回原因：${item.withdrawnReason}` : ""}</p>
        </div>
        {!item.withdrawnAt ? <ButtonRow>
          <Button variant="outline" size="sm" onClick={onReplace}>替换</Button>
          <Button variant="ghost" size="sm" onClick={() => setWithdrawing(true)}>撤回</Button>
        </ButtonRow> : null}
      </div>
      {withdrawing ? (
        <ConfirmPanel
          title={`撤回「${item.title}」？`}
          consequence="撤回之后会员首页不再出现这一条；它仍留在年历记录里，写明原因。"
          confirmLabel="撤回"
          busy={busy}
          confirmDisabled={!reason.trim()}
          onCancel={() => setWithdrawing(false)}
          onConfirm={() => void withdraw()}
        >
          <Field label="撤回原因" hint="至多 120 字"><Input value={reason} maxLength={120} onChange={(e) => setReason(e.target.value)} /></Field>
        </ConfirmPanel>
      ) : null}
      {error ? <Banner tone="danger">{error}</Banner> : null}
    </li>
  );
}

function NodeForm({ year, draft, onCancel, onSaved }: { year: number; draft: Draft; onCancel: () => void; onSaved: () => void }) {
  const organizations = usePublishOrganizations();
  const [form, setForm] = useState(draft);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (key: keyof Draft) => (value: string) => setForm((f) => ({ ...f, [key]: value }));
  const monthsOk = Number(form.endMonth) >= Number(form.startMonth);
  const ready = form.organizationId && form.title.trim().length >= 2 && form.audienceLabel.trim().length >= 2 && form.sourceRef.trim().length >= 2 && monthsOk;
  const save = async () => {
    setBusy(true); setError("");
    try {
      await api("/api/v1/calendar", {
        method: "POST",
        body: {
          commandId: commandId(), organizationId: form.organizationId, year, startMonth: Number(form.startMonth), endMonth: Number(form.endMonth),
          title: form.title.trim(), audienceLabel: form.audienceLabel.trim(), ...(form.note.trim() ? { note: form.note.trim() } : {}), sourceRef: form.sourceRef.trim(),
          ...(draft.supersedes ? { supersedesId: draft.supersedes.id } : {})
        }
      });
      onSaved();
    } catch (caught) {
      setError(reasonText(caught));
    } finally {
      setBusy(false);
    }
  };
  return (
    <DeskCard
      title={draft.supersedes ? `替换「${draft.supersedes.title}」` : `录入 ${year} 年的节点`}
      description={draft.supersedes ? "保存后旧节点撤回（原因记为「由新节点替换」），新节点指向它。" : "只录已经定下来的节点；拿不准的不要录，会员首页会原样显示。"}
    >
      <FormSection step="1" title="哪个组织、什么时候">
        <FieldGrid>
          <Field label="组织" hint="只列出你有会务协调授权的组织；会员看得到本组织及上级录的节点">
            <SelectField value={form.organizationId} onChange={set("organizationId")} placeholder="选择组织" options={organizations} />
          </Field>
          <Field label="开始月份"><SelectField value={form.startMonth} onChange={set("startMonth")} placeholder="月份" options={MONTHS} /></Field>
          <Field label="结束月份" hint={monthsOk ? undefined : "结束不能早于开始"}><SelectField value={form.endMonth} onChange={set("endMonth")} placeholder="月份" options={MONTHS} /></Field>
        </FieldGrid>
      </FormSection>
      <FormSection step="2" title="是什么">
        <FieldGrid>
          <Field label="名称" hint="2—40 字" wide><Input value={form.title} maxLength={40} onChange={(e) => set("title")(e.target.value)} placeholder="例如：第三季度主委会议" /></Field>
          <Field label="面向谁" hint="2—20 字，给会员看的一句话"><Input value={form.audienceLabel} maxLength={20} onChange={(e) => set("audienceLabel")(e.target.value)} /></Field>
          <Field label="补充一句" hint="至多 120 字，选填"><Input value={form.note} maxLength={120} onChange={(e) => set("note")(e.target.value)} /></Field>
          <Field label="依据" hint="出自哪份文件，2—120 字" wide><Input value={form.sourceRef} maxLength={120} onChange={(e) => set("sourceRef")(e.target.value)} /></Field>
        </FieldGrid>
      </FormSection>
      <Note>会员首页只显示与当前季度相交的节点，按开始月份排。</Note>
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <ButtonRow>
        <Button variant="ghost" onClick={onCancel}>取消</Button>
        <Button disabled={!ready || busy} onClick={() => void save()}>{draft.supersedes ? "替换" : "录入"}</Button>
      </ButtonRow>
    </DeskCard>
  );
}
